import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useHints } from '../contexts/HintsContext';

const POP_MAX_W = 260;
const MARGIN = 12;
const GAP = 6;

export default function HelpHint({ text, label, className = '' }) {
  const { enabled } = useHints();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current || !popRef.current) return;
    const btn = btnRef.current.getBoundingClientRect();
    const popH = popRef.current.offsetHeight;
    const popW = Math.min(POP_MAX_W, window.innerWidth - 2 * MARGIN);

    const center = btn.left + btn.width / 2;
    let left = center - popW / 2;
    if (left < MARGIN) left = MARGIN;
    if (left + popW > window.innerWidth - MARGIN) left = window.innerWidth - MARGIN - popW;

    const spaceBelow = window.innerHeight - btn.bottom - MARGIN;
    const spaceAbove = btn.top - MARGIN;
    const placeAbove = popH > spaceBelow && spaceAbove > spaceBelow;
    const top = placeAbove ? Math.max(MARGIN, btn.top - GAP - popH) : btn.bottom + GAP;

    setPos({ top, left, width: popW });
  }, [open]);

  if (!enabled) return null;

  return (
    <span className={className} style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
      <button
        ref={btnRef}
        type="button"
        aria-label={label || 'Pomoc'}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '16px', height: '16px', padding: '0 2px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: open ? 'var(--acc)' : 'var(--acc2)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '14px', fontWeight: 800, lineHeight: 1,
          letterSpacing: 0, textTransform: 'none',
          transition: 'color 0.12s',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.color = 'var(--acc)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.color = 'var(--acc2)'; }}
      >
        ?
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: pos ? `${pos.top}px` : '-9999px',
            left: pos ? `${pos.left}px` : '0px',
            width: pos ? `${pos.width}px` : `${POP_MAX_W}px`,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 9999,
            background: 'var(--bg-nav)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            padding: '10px 12px',
            fontSize: '11px',
            lineHeight: 1.45,
            color: 'var(--ink2)',
            fontWeight: 400,
            letterSpacing: 0,
            textTransform: 'none',
            whiteSpace: 'normal',
            textAlign: 'left',
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </span>
  );
}
